import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { setModalCountToZero, doneRequestSequentialModal } from 'shared-modules/redux/actions';
import { push } from 'shared-modules/redux/actions/routerActions';
import {
  updateIsFirstLogin,
  updateUserLearnTriautoStatusRequest,
  updateUserLearnTriautoStatus,
} from 'shared-modules/redux/actions/settingsActions';
import { STEPS } from 'shared-modules/constants/tutorial';
import { SEQUENTIAL_MODALS } from 'shared-modules/constants/sequentialModals';
import { LEARN_TRIAUTO_MENU, LEARN_TRIAUTO_STATUS } from 'shared-modules/constants';
import { HOME } from '../../../../constants';
import CustomButton from '../../../../components/CustomButton';
import { Button } from '../../../../components';
import styles from './tutorial.module.scss';
import { renderTextWithLineBreaks } from '../../../../services';

import tutorialStep1Image from '../../../../assets/tutorial/step1pc.png';
import tutorialStep2Image from '../../../../assets/tutorial/step2pc.png';
import tutorialStep3Image from '../../../../assets/tutorial/step3pc.png';
import tutorialStep4Image from '../../../../assets/tutorial/step4pc.png';
import tutorialStep5Image from '../../../../assets/tutorial/step5pc.png';
import tutorialStep6Image from '../../../../assets/tutorial/step6pc.png';
import tutorialStep7Image from '../../../../assets/tutorial/step7pc.png';
import tutorialStep8Image from '../../../../assets/tutorial/step8pc.png';

const STEP_IMAGES = {
  1: tutorialStep1Image,
  2: tutorialStep2Image,
  3: tutorialStep3Image,
  4: tutorialStep4Image,
  5: tutorialStep5Image,
  6: tutorialStep6Image,
  7: tutorialStep7Image,
  8: tutorialStep8Image,
};

const LAST_STEP = 8;

const STEP_LAYOUT = {
  2: { top: 62, height: 104, width: 358 },
  7: { top: 24, height: 156, width: 355 },
  default: { top: 24, height: 176, width: 200 },
};

const handleStopPropagation = (e) => e.stopPropagation();
const getStepLayout = (step) => STEP_LAYOUT[step] || STEP_LAYOUT.default;

export const Tutorial = memo(({ isOpen, closeModal }) => {
  const dispatch = useDispatch();
  const { beginnerReadStatusList } = useSelector((state) => state.settings.learnTriautoStatus);

  const [step, setStep] = useState(1);
  const [isInterrupt, setInterrupt] = useState(false);

  const next = useCallback(() => {
    setStep(step + 1);
  }, [step, setStep]);

  const previous = useCallback(() => {
    setStep(step - 1);
  }, [step, setStep]);

  const interrupt = useCallback(() => {
    setInterrupt(true);
  }, [setInterrupt]);

  const resume = useCallback(() => {
    setInterrupt(false);
  }, [setInterrupt]);

  const exit = useCallback(() => {
    setInterrupt(false);
    closeModal();
    dispatch(setModalCountToZero());
    dispatch(updateIsFirstLogin());
    dispatch(doneRequestSequentialModal({ modalId: SEQUENTIAL_MODALS.TutorialModal }));
    dispatch(push(`/${HOME}`));
  }, [closeModal, dispatch]);

  const finish = useCallback(() => {
    closeModal();
    dispatch(setModalCountToZero());
    dispatch(doneRequestSequentialModal({ modalId: SEQUENTIAL_MODALS.TutorialModal }));
    dispatch(push(`/${HOME}`));
  }, [dispatch, closeModal]);

  useEffect(() => {
    Object.values(STEP_IMAGES).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (
        beginnerReadStatusList[LEARN_TRIAUTO_MENU.BEGINNER.STEPS[0].key] === LEARN_TRIAUTO_STATUS.READ_STATUS.UNREAD
      ) {
        dispatch(updateUserLearnTriautoStatus({ key: LEARN_TRIAUTO_MENU.BEGINNER.STEPS[0].key }));
        dispatch(updateUserLearnTriautoStatusRequest());
      }
    }
  }, [beginnerReadStatusList, dispatch, isOpen]);

  const body = useMemo(() => {
    const { top, height, width } = getStepLayout(step);
    const stepData = STEPS[step];
    if (isInterrupt) {
      return (
        <div className={styles.exitBody}>
          <div className={styles.exitCloseButton}>
            <CustomButton onClick={resume} width={24} isSmall className={styles.customButton}>
              <i className={classNames('material-icons', styles.exitIcon)}>close</i>
            </CustomButton>
          </div>
          <h2 className={styles.exitTitle}>終了してもよろしいでしょうか</h2>
          <p className={styles.exitMessage}>チュートリアルは画面左上のメニューからいつでもご確認いただけます。</p>
          <div className={styles.step}>
            <Button onClick={resume} secondary width={170}>
              再開する
            </Button>
            <Button onClick={exit} width={170}>
              終了する
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.content}>
        <div className={styles.closeButton}>
          <CustomButton onClick={interrupt} width={24} isSmall className={styles.customButton}>
            <i className={classNames('material-icons', styles.closeIcon)}>close</i>
          </CustomButton>
        </div>

        {step > 1 && (
          <div className={styles.backButton}>
            <CustomButton onClick={previous} width={24} isSmall className={styles.customButton}>
              <i className={classNames('material-icons', styles.backIcon)}>arrow_back_ios</i>
            </CustomButton>
          </div>
        )}

        <div className={styles.imageWrapper}>
          <div className={styles.image} style={{ top }}>
            <img src={STEP_IMAGES[step]} style={{ height, width }} alt="" />
          </div>
        </div>

        <div className={styles.stepBody}>
          <div className={styles.titleWrapper}>
            <h2 className={styles.stepTitle}>{renderTextWithLineBreaks(stepData.title)}</h2>
          </div>
          <p className={styles.stepMessage}>{renderTextWithLineBreaks(stepData.message)}</p>
        </div>

        <div className={styles.dots}>
          {Object.keys(STEPS).map((index) => (
            <span key={index} className={classNames(styles.dot, { [styles.active]: Number(index) === step })} />
          ))}
        </div>

        <div className={styles.step}>
          {step < LAST_STEP ? (
            <Button onClick={next} width={170}>
              次へ
            </Button>
          ) : (
            <Button onClick={finish} width={256}>
              はじめる
            </Button>
          )}
        </div>
      </div>
    );
  }, [step, isInterrupt, interrupt, previous, next, resume, exit, finish]);

  return (
    <Modal
      show={isOpen}
      centered
      onClick={handleStopPropagation}
      dialogClassName={classNames(styles.modal)}
      aria-labelledby="contained-modal-title-vcenter"
      backdropClassName={classNames(styles.backdrop, 'backdrop')}
      style={{ zIndex: 1101 }}
    >
      <Modal.Body bsPrefix={styles.body}>
        <div className={styles.container}>{body}</div>
      </Modal.Body>
    </Modal>
  );
});

Tutorial.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
};
